import { Login } from "@xola/ui-kit";
import { useDocumentVisibility, useMount } from "ahooks";
import { AxiosError } from "axios";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { MobileAppsMessages, XolaMobileApp } from "types";
import { xolaUri } from "../api/xolaUri";
import { AuthMethod } from "../auth/authMethod";
import { createAuthorizationRedirect } from "../auth/oauthSessionHelper";
import { useAccount } from "../hooks/useAccount";
import { OAuthSessionStatus, useAuthStore } from "../stores/authStore";
import { log } from "../utils/log";
import { buildEnvironmentBadge } from "./environmentBadge";
import { LoginSkeleton } from "./LoginSkeleton";
import { AccountErrorScreen, IdleLogoutNotice, SignInFailedScreen, SignInSkeleton } from "./SignInSkeleton";
import { clearIdleSession } from "@/auth/idleSession";
import { SignOutReason } from "@/auth/signOut";
import { useXolaMobileApps } from "@/components/MobileApps/XolaMobileAppsContext";
import { getAccountLockMessage, isAccountLockedResponse } from "@/utils/accountLock";

const getErrorMessage = (error: AxiosError | null) => {
    const response = error?.response;
    const status = response?.status;

    // Ahead of the status mapping, since a 423 otherwise means a concurrent-edit conflict.
    if (response && isAccountLockedResponse(response)) {
        return getAccountLockMessage(response);
    }

    if (status) {
        if (status === 401) {
            return "Invalid Credentials";
        }

        if (status === 403) {
            return "Access Denied";
        }
    }

    return error?.message;
};

const getRememberMe = () => {
    const rememberMe = localStorage.getItem("rememberMe");
    if (rememberMe) {
        return rememberMe === "true";
    }

    return true;
};

const setRememberMe = (shouldRemember: boolean) => {
    localStorage.setItem("rememberMe", String(shouldRemember));
};

export const LoginPage = () => {
    const shouldRemember = getRememberMe();
    const [loginValues, setLoginValues] = useState({ email: "", password: "", shouldRemember });
    const { sendMessage, xolaMobileApp } = useXolaMobileApps();
    const authMethod = useAuthStore((state) => state.method);
    const authStatus = useAuthStore((state) => state.status);

    const logoutMessageSent = useRef(false);
    // Read once, then dropped from the address bar so a refresh or bookmark does not show the notice again.
    const [isIdleLogout] = useState(
        () => new URLSearchParams(window.location.search).get("reason") === SignOutReason.IDLE,
    );

    useMount(() => {
        if (isIdleLogout) {
            const url = new URL(window.location.href);
            url.searchParams.delete("reason");
            window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
        }
    });

    const {
        data: user,
        isLoading,
        error,
        refetch,
    } = useAccount(loginValues, {
        onSuccess: () => {
            if (loginValues.email && loginValues.password) {
                log("Login successful", { user: { email: loginValues.email } });
            }
        },

        onError: (error) => {
            log("Login error", {
                user: { email: loginValues.email },
                error,
            });
        },
    });

    // Always force Tablet app to use native login screen
    const showingLoginForm = !user && !isLoading && !loginValues.email;
    const isTabletApp = xolaMobileApp === XolaMobileApp.XolaTablet;

    useEffect(() => {
        if (isTabletApp && showingLoginForm && !logoutMessageSent.current) {
            logoutMessageSent.current = true;
            sendMessage({ type: MobileAppsMessages.LOGOUT });
        }
    }, [isTabletApp, showingLoginForm, sendMessage]);

    // Refetch when credentials update.
    useEffect(() => {
        if (loginValues.email && loginValues.password) {
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginValues]);

    const shouldRedirectToOAuthServer = authMethod === AuthMethod.OAUTH && authStatus === OAuthSessionStatus.SIGNED_OUT;
    const visibility = useDocumentVisibility();
    const hasRedirected = useRef(false);

    // Every open tab signs out together, and the login server keeps one in-flight request per browser, so only the
    // tab in view starts one. Background tabs wait on the skeleton until shown.
    useEffect(() => {
        if (!shouldRedirectToOAuthServer || visibility !== "visible" || hasRedirected.current) {
            return;
        }

        hasRedirected.current = true;
        clearIdleSession();
        // Nothing else is mounted to catch this, and a rejection would leave a spinner forever.
        createAuthorizationRedirect(isIdleLogout ? SignOutReason.IDLE : undefined)
            .then((url) => window.location.assign(url))
            .catch(() => useAuthStore.getState().setStatus(OAuthSessionStatus.FAILED));
    }, [shouldRedirectToOAuthServer, visibility, isIdleLogout]);

    // The OAuth method has no form of its own: the credential is collected by the Xola login page.
    if (authMethod === AuthMethod.OAUTH) {
        if (authStatus === OAuthSessionStatus.FAILED) {
            return <SignInFailedScreen />;
        }

        if (error) {
            return <AccountErrorScreen message={getErrorMessage(error)} />;
        }

        // Otherwise a token is held and the dashboard's own data is still loading.
        return shouldRedirectToOAuthServer ? <SignInSkeleton /> : <LoginSkeleton />;
    }

    // User is logged in, no need to show anything.
    if (user) {
        return null;
    }

    // Trying to authenticate the logged in user, show full screen loader.
    if (isLoading && !loginValues.email) {
        return <LoginSkeleton />;
    }

    const handleSubmit = (values: any) => {
        clearIdleSession();
        setRememberMe(values.shouldRemember);
        setLoginValues(values);
    };

    return (
        <Login
            isLoading={isLoading}
            error={isLoading ? null : getErrorMessage(error)}
            defaultValues={loginValues}
            passwordResetUrl={`${xolaUri}/resetting/form`}
            backgroundType="x2"
            label={
                <div className={clsx("space-y-4", isIdleLogout && "-mb-6 md:-mb-14")}>
                    {buildEnvironmentBadge()}
                    {isIdleLogout && <IdleLogoutNotice />}
                </div>
            }
            onSubmit={handleSubmit}
        />
    );
};
